import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { getMyOrganization, submitAdminRequest } from '../../api/organizationApi';
import { useAuth } from '../../contexts/AuthContext';
import './SchoolPlan.css';

export const SCHOOL_LEVEL_OPTIONS = [
  { id: 'elementary', label: '초등' },
  { id: 'middle', label: '중학교' },
  { id: 'high', label: '고등학교' },
  { id: 'other', label: '기타' },
];

const BENEFITS = [
  { title: '빈 자리와 초대 링크', body: '몇 분이 합류했는지 보고, 링크 하나로 나머지 선생님을 모셔요.' },
  { title: '선생님별 합류 현황', body: '누가 언제 합류했는지, 이번 주에 써 보셨는지 한눈에 봐요.' },
  { title: '기능별 사용 합계', body: '생기부·채점·평가계획서를 이번 주, 이번 달 몇 건 썼는지 봐요.' },
];

const emptyForm = (email = '') => ({
  school_name: '',
  school_level: '',
  teacher_count: '',
  purchase_email: email,
  purchase_ref: '',
  phone: '',
  message: '',
});

const SchoolAdminApplyPage = () => {
  const { currentUser } = useAuth();
  const [state, setState] = useState({ loading: true, organization: null, request: null });
  const [form, setForm] = useState(() => emptyForm(currentUser?.email || ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const me = await getMyOrganization(currentUser);
      setState({ loading: false, organization: me?.organization || null, request: me?.admin_request || null });
    } catch (_error) {
      setState({ loading: false, organization: null, request: null });
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!form.school_name.trim()) {
      setError('학교 이름을 적어 주세요.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitAdminRequest(currentUser, {
        ...form,
        school_name: form.school_name.trim(),
        teacher_count: Number(form.teacher_count) || 0,
      });
      setState((prev) => ({ ...prev, request: { status: 'pending', school_name: form.school_name.trim() } }));
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const { loading, organization, request } = state;
  const isAdmin = organization?.role === 'admin';
  const showForm = !loading && !isAdmin && request?.status !== 'pending' && request?.status !== 'approved';

  return (
    <main className="school-page">
      <Helmet>
        <title>학교 관리자 신청 | 왓퀴즈</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="school-hero">
        <p className="school-eyebrow">학교(엔터프라이즈)</p>
        <h1>학교 관리자 화면을 열어 드려요</h1>
        <p className="school-hero-sub">12개월 · 선생님 10명 이상 구매한 학교의 대표 선생님이 신청하면, 운영팀이 구매 내역을 확인하고 지정해 드려요.</p>
      </header>

      <ul className="school-benefits" aria-label="관리자 화면에서 할 수 있는 일">
        {BENEFITS.map((item) => (
          <li key={item.title}>
            <strong>{item.title}</strong>
            <span>{item.body}</span>
          </li>
        ))}
      </ul>
      <p className="school-privacy-note">선생님이 만든 생기부·채점 내용은 관리자에게 보이지 않아요. 건수만 보여요.</p>

      {loading && <div className="school-card school-skeleton" aria-label="불러오는 중" />}

      {!loading && isAdmin && (
        <section className="school-card school-status-card">
          <h2>{organization.name || '우리 학교'} 관리자예요</h2>
          <p>관리자 화면에서 빈 자리와 초대 링크를 확인하세요.</p>
          <Link className="school-btn-primary" to="/school-admin">관리자 화면 열기</Link>
        </section>
      )}

      {!loading && !isAdmin && request?.status === 'pending' && (
        <section className="school-card school-status-card" data-testid="school-apply-pending">
          <span className="school-status-dot" aria-hidden="true" />
          <h2>신청을 받았어요</h2>
          <p>
            {request.school_name ? `${request.school_name} · ` : ''}
            운영팀이 구매 내역을 확인하면 이 화면에서 관리자 화면을 열 수 있어요.
          </p>
        </section>
      )}

      {!loading && !isAdmin && request?.status === 'approved' && (
        <section className="school-card school-status-card">
          <h2>관리자로 지정됐어요</h2>
          <Link className="school-btn-primary" to="/school-admin">관리자 화면 열기</Link>
        </section>
      )}

      {showForm && (
        <form className="school-card school-form" onSubmit={handleSubmit} noValidate>
          {request?.status === 'rejected' && (
            <p className="school-form-notice" role="status">
              지난 신청은 확인하지 못했어요{request.reject_reason ? ` (${request.reject_reason})` : ''}. 내용을 고쳐 다시 보내 주세요.
            </p>
          )}

          <label className="school-field">
            <span>학교 이름</span>
            <input value={form.school_name} onChange={update('school_name')} placeholder="가나초등학교" maxLength={80} required />
          </label>

          <fieldset className="school-field">
            <legend>학교급</legend>
            <div className="school-segment" role="radiogroup" aria-label="학교급">
              {SCHOOL_LEVEL_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={form.school_level === option.id}
                  className={form.school_level === option.id ? 'is-selected' : ''}
                  onClick={() => setForm((prev) => ({ ...prev, school_level: option.id }))}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="school-field">
            <span>함께 쓸 선생님 수</span>
            <input type="number" inputMode="numeric" min="0" value={form.teacher_count} onChange={update('teacher_count')} placeholder="20" />
          </label>

          <label className="school-field">
            <span>구매할 때 적은 이메일</span>
            <input type="email" value={form.purchase_email} onChange={update('purchase_email')} maxLength={120} />
            <small>구매 내역과 맞춰 보고 관리자로 지정해 드려요.</small>
          </label>

          <label className="school-field">
            <span>주문번호·견적서 번호 <em>(선택)</em></span>
            <input value={form.purchase_ref} onChange={update('purchase_ref')} maxLength={120} />
          </label>

          <label className="school-field">
            <span>연락처 <em>(선택)</em></span>
            <input type="tel" value={form.phone} onChange={update('phone')} maxLength={30} />
            <small>확인이 더 필요할 때만 연락드려요.</small>
          </label>

          <label className="school-field">
            <span>남길 말 <em>(선택)</em></span>
            <textarea value={form.message} onChange={update('message')} maxLength={500} rows={3} />
          </label>

          {error && <p className="school-error" role="alert">{error}</p>}

          <button type="submit" className="school-btn-primary school-btn-block" disabled={submitting}>
            {submitting ? '보내는 중…' : '관리자 신청하기'}
          </button>
          <p className="school-form-foot">
            아직 구매 전이라면 <Link to="/pricing#compare">요금제 비교</Link>에서 견적부터 받아 보세요.
          </p>
        </form>
      )}
    </main>
  );
};

export default SchoolAdminApplyPage;
